/**
 * Available background colors
 * Each value should have a corresponding class in avatar.module.css
 */
export const BACKGROUND_COLORS = [
  'purple',
  'mint',
  'mustard',
  'peach',
  'blue',
  'green',
  'pink',
  'gray',
] as const;

export type AvatarBackgroundColor = (typeof BACKGROUND_COLORS)[number];

/**
 * Possible values for "random" background color - used when deriving color from name.
 *
 * This is a hacky workaround to avoid visual regression, matching the legacy (broken) deterministic logic:
 * - Double 'purple' value
 * - Missing 'gray' value
 *
 * It should be cleaned up once the Design System team moves forward on the new UI.
 * @see https://qonto.slack.com/archives/C024QTW5Q64/p1771316522863879
 */
export const RANDOM_BACKGROUND_COLORS = [
  'purple',
  'purple',
  'mint',
  'mustard',
  'peach',
  'blue',
  'green',
  'pink',
] as const;

export type AvatarRandomBackgroundColor = (typeof RANDOM_BACKGROUND_COLORS)[number];

/**
 * Available sizes
 * Each value should have a corresponding class in avatar.module.css
 */
export const AVATAR_SIZES = ['64', '56', '48', '40', '32', '30', '24', '16'] as const;
export type AvatarSize = (typeof AVATAR_SIZES)[number];

/**
 * Corner icon overlay dimensions (bottom-right of Avatar).
 * Each value should have a corresponding class in avatar.module.css
 */
export const AVATAR_ICON_SIZES = ['16', '20', '24'] as const;
export type AvatarIconSize = (typeof AVATAR_ICON_SIZES)[number];

/**
 * Corner icon size for a given avatar width (spec-driven, not configurable on `Avatar`).
 * - 16–30px avatar: no overlay (`null`).
 * - 32px avatar: 16px icon.
 * - Larger than 32px: 20px icon.
 */
export function getIconSizeForAvatarSize(avatarSize: AvatarSize): AvatarIconSize | null {
  const size = Number(avatarSize);
  if (size <= 30) {
    return null;
  }
  if (size === 32) {
    return '16';
  }
  return '20';
}
